import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  provideTranslocoScope,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { MzicSvgComponent } from '@mzic/mzic-components';
import { ConnectionAcquisitionDto } from '../../../interfaces/collection-revision-connection.interface';
import { ConnectionEntity } from '../../../interfaces/collection.interface';
import {
  ResolvedCredentialField,
  resolveCredentialFieldLabel,
  resolveCredentialSchema,
} from '../../../utils/credential-schema-resolver';
import { ConnectionEntityBrandChipComponent } from '../../connection-entity-brand-chip/connection-entity-brand-chip.component';

@Component({
  selector: 'lib-completed-connection-step',
  imports: [
    CommonModule,
    TranslocoModule,
    MzicSvgComponent,
    ConnectionEntityBrandChipComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './completed-connection-step.component.html',
  styleUrl: './completed-connection-step.component.scss',
})
export class CompletedConnectionStepComponent {
  private readonly translocoService = inject(TranslocoService);

  readonly protectedCredentialPlaceholder = '••••••••';

  @Input({ required: true }) acquisition!: ConnectionAcquisitionDto;
  @Input() entity: ConnectionEntity | null = null;
  @Input() loadingEntity = false;

  @Output() back = new EventEmitter<void>();
  @Output() continue = new EventEmitter<void>();

  get entityName(): string {
    return (
      this.entity?.display_name ||
      this.acquisition.entity.entity_name ||
      this.translocoService.translate(
        'lang.connection_completed_entity_fallback',
      )
    );
  }

  get entityIconUrl(): string | null {
    return this.entity?.icon_url ?? null;
  }

  get credentialFields(): ResolvedCredentialField[] {
    return this.entity
      ? resolveCredentialSchema(this.entity.credential_schema).fields
      : [];
  }

  get hasResolvedCredentialFields(): boolean {
    return this.credentialFields.length > 0;
  }

  getFieldLabel(field: ResolvedCredentialField): string {
    return resolveCredentialFieldLabel(field, (key) =>
      this.translocoService.translate(`lang.${key}`),
    );
  }

  isPasswordField(field: ResolvedCredentialField): boolean {
    return field.semantic === 'password';
  }
}
